import { AlertCircle, ArrowLeft, Bot, Database, FolderOpen, Loader2, MessageSquare, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArtifactsTab } from "@/components/project-hub/ArtifactsTab";
import { HubTab } from "@/components/project-hub/HubTab";
import { MembersTab } from "@/components/project-hub/MembersTab";
import { ResourcesTab } from "@/components/project-hub/ResourcesTab";
import { SettingsTab } from "@/components/project-hub/SettingsTab";
import { getStatusConfig, type TabId } from "@/components/project-hub/utils";
import { useConfirm, useToast } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { projectService } from "@/lib/services";
import type { ProjectHub as ProjectHubType } from "@/types";

export function ProjectHub() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [hub, setHub] = useState<ProjectHubType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("hub");

  const canEdit =
    hub?.currentUserRole === "owner" ||
    hub?.currentUserRole === "editor" ||
    hub?.currentUserRole === "platform_admin" ||
    user?.isPlatformAdmin;

  const isOwner =
    hub?.currentUserRole === "owner" || hub?.currentUserRole === "platform_admin" || user?.isPlatformAdmin;

  const loadHub = useCallback(
    async (silent = false) => {
      if (!projectId) return;
      try {
        if (!silent) setLoading(true);
        const response = await projectService.getHub(projectId);
        setHub(response.data);
      } catch (error) {
        console.error("Failed to load project hub:", error);
        if (!silent) showToast("Failed to load project", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [projectId, showToast],
  );

  useEffect(() => {
    loadHub();
  }, [loadHub]);

  const handleRemoveResource = async (resourceUuid: string) => {
    if (!projectId) return;
    const confirmed = await confirm({
      title: "Remove Resource",
      message: "Are you sure you want to remove this resource from the project?",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await projectService.removeResource(projectId, resourceUuid);
      showToast("Resource removed", "success");
      loadHub();
    } catch (err) {
      console.error("Failed to remove resource:", err);
      showToast("Failed to remove resource", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!hub) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-content-primary mb-2">Project not found</h2>
        <button
          onClick={() => navigate("/projects")}
          className="mt-4 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(hub.status);

  const tabs: { id: TabId; label: string; ownerOnly?: boolean }[] = [
    { id: "hub", label: "Hub" },
    { id: "resources", label: "Resources" },
    { id: "artifacts", label: "Artifacts" },
    { id: "members", label: `Members (${hub.stats.memberCount})` },
    { id: "settings", label: "Settings", ownerOnly: true },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate("/projects")}
            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-4 flex-1">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${hub.color}20` }}
            >
              <FolderOpen className="h-6 w-6" style={{ color: hub.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-content-primary truncate">{hub.name}</h1>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}
                >
                  {statusConfig.label}
                </span>
                {hub.visibility === "internal" && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-brand-primary/20 text-brand-primary rounded-full">
                    Internal
                  </span>
                )}
              </div>
              {hub.description && <p className="text-content-tertiary mt-0.5 truncate">{hub.description}</p>}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.memberCount} {hub.stats.memberCount === 1 ? "member" : "members"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.agentCount} {hub.stats.agentCount === 1 ? "agent" : "agents"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.dataSourceCount} {hub.stats.dataSourceCount === 1 ? "data source" : "data sources"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-content-quaternary" />
            <span className="text-content-tertiary">
              {hub.stats.chatCount} {hub.stats.chatCount === 1 ? "conversation" : "conversations"}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-4 -mb-px" role="tablist" aria-label="Project hub sections">
          {tabs
            .filter((tab) => !tab.ownerOnly || isOwner)
            .map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`tabpanel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg border transition-colors ${
                  activeTab === tab.id
                    ? "border-border-primary border-b-transparent bg-surface-primary text-content-primary"
                    : "border-transparent text-content-tertiary hover:text-content-secondary"
                }`}
              >
                {tab.label}
              </button>
            ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === "hub" && (
          <HubTab
            hub={hub}
            navigate={navigate}
            canEdit={canEdit ?? false}
            projectId={projectId!}
            onRefresh={() => loadHub(true)}
          />
        )}
        {activeTab === "resources" && (
          <ResourcesTab
            hub={hub}
            navigate={navigate}
            canEdit={canEdit ?? false}
            onRemoveResource={handleRemoveResource}
            onRefresh={() => loadHub(true)}
          />
        )}
        {activeTab === "artifacts" && (
          <ArtifactsTab projectId={projectId!} projectUuid={hub.uuid} navigate={navigate} />
        )}
        {activeTab === "members" && (
          <MembersTab
            hub={hub}
            isOwner={isOwner ?? false}
            canEdit={canEdit ?? false}
            projectId={projectId!}
            onRefresh={() => loadHub(true)}
          />
        )}
        {activeTab === "settings" && isOwner && (
          <SettingsTab hub={hub} projectId={projectId!} onRefresh={() => loadHub(true)} />
        )}
      </div>
    </div>
  );
}
